import { useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { Navbar } from "components/layout/components/Navbar";
import { useTour } from "components/layout/components/tours/TourGuideContext";
import { useCheckPermission } from "components/layout/sidebar/hooks/useHasPermission";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useGetSubscriptionRulesStatus } from "features/home/hooks/useGetSubscriptionRulesStatus";
import { TALENT_DEMO_JOB } from "features/demo/data";
import { jobsCreationPath } from "utils/formHelpers/routeHelper";
import { openNotification } from "utils/notification";
import { FilterJob } from "../components/FilterJob";
import { JobsTable } from "../components/JobsTable";
import { SuggestedJobsTable } from "../components/SuggestedJobs";
import { useJobFormStore } from "../hooks/useJobFormStore";

type JobTab = "all" | "liked" | "bookmarked" | "suggested";

const jobDistributionBalance = (status: unknown): number | null => {
  if (!status || typeof status !== "object") return null;
  const s = status as {
    packageBalance?: number | null;
    rules?: Array<{ packageBalance?: number | null }>;
  };
  return s.packageBalance ?? s.rules?.[0]?.packageBalance ?? null;
};

export const RecruiterJobs = () => {
  const { userInfo } = useGetUserInfo();
  const [jobFilter, setJobFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<JobTab>("all");
  const { running } = useTour();

  const canCreateJobs = useCheckPermission("job.create");
  const { data: subscriptionRulesStatus } = useGetSubscriptionRulesStatus({
    label: "create_post",
  });
  const { data: jobDistributionStatus } = useGetSubscriptionRulesStatus({
    label: "job_distribution",
  });

  const canCreateJobBySubscription =
    subscriptionRulesStatus === undefined ||
    subscriptionRulesStatus.rules?.[0]?.status === true;

  const { resetForm } = useJobFormStore();

  const handleJob = (_id: number) => {
    /* Row action: extend if parent opens a job modal */
  };

  const handleCreateJobClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!canCreateJobBySubscription) {
      e.preventDefault();
      openNotification({
        title: "You do not have enough credits to create a job.",
        description: "Please upgrade your plan to create a job.",
        state: "error",
        duration: 4.5,
      });
      return;
    }

    resetForm();
    sessionStorage.removeItem("jobCreationFormData");
    sessionStorage.removeItem("jobCreationSkillsData");
    localStorage.removeItem("jobCreationFormData");

  };

  const tabButtonClass = (tab: JobTab) =>
    `pb-2 px-1 ${
      activeTab === tab
        ? "border-b-2 border-blue-500 text-blue-600"
        : "text-gray-500 hover:text-gray-700"
    }`;

  const isTalentTourAllJobs =
    running && !userInfo?.recruiter && activeTab === "all";

  const shareBalance = jobDistributionBalance(jobDistributionStatus);

  return (
    <DashboardLayout>
      <FilterJob open={jobFilter} handleClose={() => setJobFilter(false)} />
      <Navbar
        title="Job"
        description={userInfo?.recruiter ? "View and Manage Jobs" : "Job Board"}
      />

      {userInfo?.recruiter ? (
        <div className="flex justify-end gap-2">
          {canCreateJobs && (
            <Link
              to={jobsCreationPath()}
              onClick={handleCreateJobClick}
              className="button flex items-center gap-x-2 create-job-button"
            >
              <i className="ri-checkbox-blank-circle-fill text-xs" />
              <span>Create Job</span>
            </Link>
          )}
        </div>
      ) : (
        <div className="flex gap-4 mb-6 border-b border-gray-200">
          <button
            type="button"
            className={tabButtonClass("all")}
            onClick={() => setActiveTab("all")}
          >
            All Jobs
          </button>
          <button
            type="button"
            className={tabButtonClass("liked")}
            onClick={() => setActiveTab("liked")}
          >
            Liked Jobs
          </button>
          <button
            type="button"
            className={tabButtonClass("bookmarked")}
            onClick={() => setActiveTab("bookmarked")}
          >
            Bookmarked Jobs
          </button>
          <button
            type="button"
            className={tabButtonClass("suggested")}
            onClick={() => setActiveTab("suggested")}
          >
            Suggested Jobs
          </button>
        </div>
      )}

      <div className="bg-gray-100 px-4 py-3 mt-8 rounded-md">
        <div className="flex gap-3 flex-col md:flex-row md:justify-between md:items-center">
          <div className="flex items-center">
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <i className="ri-search-line text-xl text-gray-500" />
              </div>
              <input
                type="search"
                id="job-search-input"
                className="border focus:outline-none border-gray-300 text-gray-900 text-sm rounded-lg block w-full pl-10 p-2.5 pr-24"
                placeholder="Search Jobs"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div>
            <button
              type="button"
              id="job-filter-button"
              className="transparentButton flex items-center gap-x-2 bg-white"
              onClick={() => setJobFilter(true)}
            >
              <i className="ri-filter-3-line text-base" />
              <span>Filters</span>
            </button>
          </div>
        </div>
      </div>

      {isTalentTourAllJobs ? (
        <JobsTable
          handleJob={handleJob}
          searchTerm={searchTerm}
          jobDistributionPackageCount={shareBalance}
          activeTab={activeTab}
          demoMode={running}
          demoData={[TALENT_DEMO_JOB]}
        />
      ) : activeTab === "suggested" ? (
        <SuggestedJobsTable searchTerm={searchTerm} handleJob={handleJob} />
      ) : (
        <JobsTable
          handleJob={handleJob}
          searchTerm={searchTerm}
          jobDistributionPackageCount={shareBalance}
          activeTab={activeTab}
          demoMode={!!running}
          demoData={[TALENT_DEMO_JOB]}
        />
      )}
    </DashboardLayout>
  );
};
